import React, { useState } from 'react';
import { FileVideo, Book, FileText, Link, Clock, User, HardDrive, Eye, Edit } from 'lucide-react';
import { GoDeviceCameraVideo } from "react-icons/go";
import { FiBookOpen } from "react-icons/fi";
import DetailsTab from './DetailsTab';
import useContentManagementStore from '@/stores/AdminStore/ContentManagementStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import './LibraryTab.css';

// Icon mapping based on content type
const getIconByType = (type: 'VIDEO' | 'SCORM' | 'PDF' | 'LINK'): React.ReactNode => {
  const iconMap = {
    VIDEO: <GoDeviceCameraVideo size={48} />,
    SCORM: <FiBookOpen size={48} />,
    PDF: <FileText size={48} />,
    LINK: <Link size={48} />
  };
  return iconMap[type];
};

const LibraryTab: React.FC = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [selectedContent, setSelectedContent] = useState<string | null>(null);

  const {
    libraryContent,
    loading,
    error,
    fetchLibraryContent,
  } = useContentManagementStore();

  const handleViewDetails = (id: string) => {
    setSelectedContent(id);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedContent(null);
  };

  if (showDetails && selectedContent) {
    return <DetailsTab contentId={selectedContent} onClose={handleCloseDetails} />;
  }

  if (loading && !libraryContent) {
    return (
      <div className="s-admin-library-container">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !libraryContent) {
    return (
      <div className="s-admin-library-container">
        <ErrorDisplay
          title="Failed to Load Library Content"
          message={error}
          type="error"
          showRetry
          onRetry={fetchLibraryContent}
        />
      </div>
    );
  }

  return (
    <div className="s-admin-library-container">
      <div className="s-admin-library-content-grid">
        {libraryContent && libraryContent.map((item) => (
          <div key={item.id} className="s-admin-library-content-card">
            <div className={`s-admin-library-status-badge s-admin-library-status-${item.status.toLowerCase()}`}>
              {item.status}
            </div>
            <div className="s-admin-library-content-icon">{getIconByType(item.type)}</div>
            <h3 className="s-admin-library-content-title">{item.title}</h3>
            <span className={`s-admin-library-type-badge s-admin-library-type-${item.type.toLowerCase()}`}>
              {item.type}
            </span>
            <p className="s-admin-library-content-description">{item.description}</p>
            <div className="s-admin-library-content-meta">
              <div className="s-admin-library-meta-item">
                <User size={14} />
                <span>{item.author}</span>
              </div>
              <div className="s-admin-library-meta-item">
                <Clock size={14} />
                <span>{item.updated}</span>
              </div>
              <div className="s-admin-library-meta-item">
                <HardDrive size={14} />
                <span>{item.size}</span>
              </div>
            </div>
            <div className="s-admin-library-content-tags">
              {item.tags.map((tag, index) => (
                <span key={index} className="s-admin-library-tag">
                  {tag}
                </span>
              ))}
            </div>
            <div className="s-admin-library-content-actions">
              <button className="s-admin-library-btn-view" onClick={() => handleViewDetails(item.id)}>
                <Eye size={16} />
                View
              </button>
              <button className="s-admin-library-btn-edit">
                <Edit size={16} />
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="s-admin-library-pagination">
        <span className="s-admin-library-pagination-info">Showing {libraryContent?.length || 0} of {libraryContent?.length || 0} items</span>
        <div className="s-admin-library-pagination-controls">
          <button className="s-admin-library-page-btn">Previous</button>
          <button className="s-admin-library-page-btn s-admin-library-page-active">1</button>
          <button className="s-admin-library-page-btn">2</button>
          <button className="s-admin-library-page-btn">Next</button>
        </div>
      </div>
    </div>
  );
};

export default LibraryTab;
